import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Ear, MessageSquare, Shield } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
export function CommunicationSkillsPage() {
  const navigate = useNavigate();
  const {
    progress
  } = useProgress();
  const modules = [{
    id: 'active-listening',
    title: 'The Art of Listening',
    icon: Ear,
    description: 'Truly hear what others are saying',
    color: 'from-blue-500 to-indigo-500',
    content: ['Active listening means focusing fully on the speaker, not planning your response', 'Make eye contact and put away distractions (yes, your phone too!)', "Don't interrupt - let them finish their thought", 'Ask clarifying questions: "What I hear you saying is..."', 'Show you\'re listening with nods, "mm-hmm," and facial expressions', 'Reflect back what you heard to make sure you understood correctly']
  }, {
    id: 'expressing-yourself',
    title: 'Speaking Up',
    icon: MessageCircle,
    description: 'Express your thoughts and feelings clearly',
    color: 'from-green-500 to-teal-500',
    content: ['Use "I" statements: "I feel..." instead of "You make me..."', 'Be specific: "When you canceled our plans, I felt disappointed"', "It's okay to take time to gather your thoughts before responding", 'Be honest but kind - you can be truthful without being hurtful', "Practice saying 'I need time to think about this' when you're unsure", 'Your feelings are valid - you have the right to express them']
  }, {
    id: 'conflict-resolution',
    title: 'Handling Disagreements',
    icon: MessageSquare,
    description: 'Resolve conflicts in healthy ways',
    color: 'from-orange-500 to-red-500',
    content: ["Stay calm - take deep breaths or take a break if you're too angry", 'Focus on the issue, not attacking the person', "Avoid words like 'always' and 'never' - they make people defensive", 'Listen to understand, not to win the argument', 'Look for compromise - relationships are about give and take', "Apologize when you're wrong - it shows strength, not weakness", "Some things aren't worth fighting about - pick your battles"]
  }, {
    id: 'assertiveness',
    title: 'Being Assertive',
    icon: Shield,
    description: 'Stand up for yourself respectfully',
    color: 'from-purple-500 to-pink-500',
    content: ['Assertive = expressing your needs while respecting others (not aggressive or passive)', 'You can say no without giving a long explanation or feeling guilty', 'Use a calm, confident tone - not apologetic or aggressive', '"I\'m not comfortable with that" is a complete sentence', "Don't let people talk over you - politely but firmly hold your ground", "Your needs and boundaries matter just as much as anyone else's", 'Practice in low-stakes situations to build confidence']
  }];
  return <div className="min-h-screen w-full bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50 dark:from-slate-900 dark:via-green-950 dark:to-teal-950 pb-20 md:pb-8" data-id="element-342">
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-green-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-343">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-344">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300" data-id="element-345">
            <ArrowLeft className="w-5 h-5" data-id="element-346" />
            <span className="font-medium" data-id="element-347">Home</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-348">
            Communication Skills
          </h1>
          <div className="w-20" data-id="element-349" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-350">
        <div className="text-center mb-8" data-id="element-351">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-352">
            Master Communication, {progress.preferences.name || 'Friend'}!
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-353">
            Learn to express yourself clearly, listen actively, and handle
            conflicts like a pro. Great communication = better relationships!
          </p>
        </div>
        <div className="space-y-6" data-id="element-354">
          {modules.map(module => {
          const Icon = module.icon;
          return <div key={module.id} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden" data-id="element-355">
                <div className={`h-24 bg-gradient-to-br ${module.color} flex items-center px-6`} data-id="element-356">
                  <Icon className="w-10 h-10 text-white mr-4" data-id="element-357" />
                  <div data-id="element-358">
                    <h3 className="text-2xl font-bold text-white" data-id="element-359">
                      {module.title}
                    </h3>
                    <p className="text-white/90 text-sm" data-id="element-360">
                      {module.description}
                    </p>
                  </div>
                </div>
                <div className="p-6" data-id="element-361">
                  <ul className="space-y-3" data-id="element-362">
                    {module.content.map((item, index) => <li key={index} className="flex items-start space-x-3" data-id="element-363">
                        <div className={`w-6 h-6 bg-gradient-to-br ${module.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-364">
                          <span className="text-white text-xs font-bold" data-id="element-365">
                            {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300" data-id="element-366">
                          {item}
                        </p>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-8 bg-green-50 dark:bg-slate-800 border-l-4 border-green-500 rounded-lg p-6" data-id="element-367">
          <h4 className="font-bold text-green-900 dark:text-green-300 mb-2" data-id="element-368">
            Communication is a Skill! 💬
          </h4>
          <p className="text-green-800 dark:text-green-200" data-id="element-369">
            Good communication doesn't come naturally to everyone - it's a skill
            you develop with practice! The more you practice active listening,
            assertiveness, and conflict resolution, the easier it becomes. Be
            patient with yourself as you learn!
          </p>
        </div>
      </div>
    </div>;
}